import { lighten } from '@material-ui/core';
import {
  countriesList,
  FullCountry,
  matchCountryAny,
} from 'app/consumer-app/views/products/add-assigned-product-details/add-assigned-product-details-utils';
import { ApiError } from 'app/core/api/api-core/api-core.types';
// import { useIdentity } from 'app/core/api/company/api-company';
import {
  Address,
  COMPANY_ADDRESS_KEYS,
} from 'app/core/api/company/api-company.types';
import { appEnv } from 'app/core/environment';
import { logSentry } from 'app/core/errors/error-utils';
import Form from 'app/shared/frames/Form';
import ClassName from 'app/styles/ClassName';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import {
  isInArray,
  isNonNullable,
  Opt,
  X,
} from 'app/tools/general.types';
import _ from 'lodash';
import { useSubscription } from 'observable-hooks';
import { useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { NEVER, noop, Observable } from 'rxjs';
import styled from 'styled-components/macro';
import { Merge } from 'type-fest';
import FormAutocomplete from '../form-input/FormAutocomplete';
import FormTextInput from '../form-input/FormTextInput';
import TextInput from '../form-input/TextInput';
import useInputForm from '../form-input/utilities/use-input-form';
import IconLib from '../icon/icon-lib';
import { PromptUnsaved, StylePageBody } from '../misc';
import {
  FetchifyAddressDetail,
  FetchifyInitConfig,
} from './fetchify.types';

const SearchBox = styled.div.attrs({
  className: ClassName.NO_AUTO_MARGIN,
})<{ $extraWidth: number }>`
  ${styledMixins.softShadowSmall}
  display: flex;
  margin: ${styledFuncs.pad()} -${p => 10 + p.$extraWidth}px ${styledFuncs.pad(2)};
  padding: 20px ${p => p.$extraWidth + 20}px 18px;
  > * {
    flex: 1;
  }
`;

const fetchifyDom = {
  search: 'iw-fetchify-dom-address-search-node',
};
const CC_APPLIED = 'cc_applied';

const StyleFetchify = styled(StylePageBody)<{ $width: number }>`
  #cc_c2a {
    margin-left: -45px;
    // this must be !important to override the width element style
    width: ${p => p.$width}px !important;
    z-index: 1300;

    // set all highlight styles
    &.c2a_accent_default ul.c2a_results li:not(.deadend):hover,
    &.c2a_accent_default ul.c2a_results li.active,
    &.c2a_accent_default .c2a_footer .progressBar,
    &.c2a_accent_default
      .mainbar
      .country_btn.country_btn_active:hover,
    &.c2a_accent_default .mainbar .cc-history > div:hover {
      background-color: ${p =>
        lighten(styledConsts.cPrimary(p), 0.2)};
    }
  }
`;
export type DetailedAddress = Merge<
  Address,
  { country: FullCountry }
>;
export type FfyCommand = 'submit' | 'reset';

type FormValues = DetailedAddress;

const addressKeyOrder: (keyof DetailedAddress)[] = [
  'addrLine1',
  'addrLine2',
  'city',
  'postcode',
  'country',
];

export const getAddressString = (addr: DetailedAddress): string =>
  _(addr)
    .entries()
    .filter(([key]) => isInArray(key, addressKeyOrder))
    .sortBy(([key]) => addressKeyOrder.findIndex(k => k === key))
    .map(1)
    .filter(isNonNullable)
    .join(', ');

type Props = {
  defaultValues?: Partial<Address>;
  error?: ApiError;
  editable?: boolean;
  lockCountryCode?: string;
  promptUnsaved?: boolean;
  extraWidth?: boolean;
  searchPlaceholder?: string;
  command$?: Observable<FfyCommand>;
  isAddressType?: boolean;
  onResultSelected?: (values: DetailedAddress) => void;
  onSubmit?: (values: DetailedAddress) => Observable<unknown> | void;
  setGetValues?: React.Dispatch<
    React.SetStateAction<() => Opt<Partial<FormValues>>>
  >;
};
export default function FetchifyAddressForm({
  defaultValues,
  error,
  editable,
  lockCountryCode,
  promptUnsaved = false,
  extraWidth = false,
  searchPlaceholder = 'Start with post/zip code or street',
  command$ = NEVER,
  isAddressType,
  onResultSelected,
  onSubmit = noop,
  setGetValues,
}: Props): JSX.Element {
  const critError = useErrorHandler();
  const fetchifyLoaded = useSelector(s => s.context.fetchifyLoaded);
  const { width = 300, ref } = useResizeDetector();
  // const { resource } = useIdentity();

  // useMemo to prevent mappedDefaults changing every render and
  // causing resetting of defaults every time
  // const mappedDefaults = useMemo(
  //   () => ({
  //     ...defaultValues,
  //     country: matchCountryAny(defaultValues?.country || locale),
  //   }),
  //   [defaultValues, locale]
  // );
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    handleSubmit,
    getValues: getHFValues,
    setValue: setHFValue,
    reset: resetHF,
    formState,
    inputProps,
  } = useInputForm<FormValues>({
    defaultValues: {
      ...defaultValues,
      country: matchCountryAny(defaultValues?.country),
    },
    apiError: error,
  });

  useEffect(() => {
    resetHF({
      ...defaultValues,
      country: matchCountryAny(defaultValues?.country),
    });
  }, [defaultValues, resetHF]);

  useEffect(() => {
    // Keep the getValues function up to date, for save state on clicking
    // 'previous page' button
    setGetValues?.(() => getHFValues);
  }, [getHFValues, setGetValues]);

  useEffect(() => {
    const ClickToAddressClass = window.clickToAddress;
    if (!fetchifyLoaded || !ClickToAddressClass) return;
    const handleResultSelected = (
      ...[, , address]: [X, X, FetchifyAddressDetail]
    ) => {
      const isoA2 = address.country.iso_3166_1_alpha_2;
      const country = matchCountryAny(isoA2);
      if (!country) {
        // async here so can't just throw error
        critError(new Error(`Country not found, code: ${isoA2}`));
        return;
      }

      const companyAddress: DetailedAddress = {
        addrLine1: address.line_1,
        addrLine2: address.line_2,
        city: address.locality,
        postcode: address.postal_code,
        country,
      };
      _.entries(companyAddress).forEach(([key, value = '']) => {
        if (!isInArray(key, COMPANY_ADDRESS_KEYS)) {
          // this should never occur unless the above array is wrong
          const err = new Error(`Address does not have key '${key}'`);
          critError(err);
          throw err;
        } else {
          // for all other scenarios, just set via hookForm
          setHFValue(key, value);
        }
      });
      onResultSelected?.(companyAddress);
    };

    const config: FetchifyInitConfig = {
      accessToken: appEnv.FETCHIFY_KEY,
      dom: fetchifyDom,
      showLogo: false,
      countrySelector: !lockCountryCode,
      defaultCountry: defaultValues?.country,
      placeholders: false,
    };
    const methods = { onResultSelected: handleResultSelected };

    const isAlreadyAttached =
      document
        .getElementsByName(fetchifyDom.search)[0]
        ?.getAttribute(CC_APPLIED) === 'true';

    if (!window.currentFetchify) {
      // create new Fetchify instance
      window.currentFetchify = new ClickToAddressClass({
        ...config,
        ...methods,
      });
    } else if (!isAlreadyAttached) {
      // attach to existing Fetchify instance
      window.currentFetchify.attach(fetchifyDom, methods);
    } else {
      // update onResultsSelected method on current instance
      window.currentFetchify.onResultSelected = handleResultSelected;
    }
  }, [
    critError,
    defaultValues?.country,
    fetchifyLoaded,
    lockCountryCode,
    onResultSelected,
    setHFValue,
  ]);

  const formSubmit = handleSubmit(onSubmit);

  useSubscription(command$, cmd => {
    // I don't know why this is registering as an `any` type.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    if (cmd === 'submit') formSubmit().catch(logSentry);
    if (cmd === 'reset') resetHF();
  });

  return (
    <>
      <PromptUnsaved
        when={promptUnsaved && !_.isEmpty(formState.touched)}
      />
      {/* + 14 in order to account for the styling adjustments on the input */}
      <StyleFetchify $width={width + 14} />
      <SearchBox $extraWidth={10 * Number(extraWidth)}>
        <TextInput
          greyPlaceholder
          placeholder={searchPlaceholder}
          label="Search address"
          icon={IconLib.SEARCH}
          name={fetchifyDom.search}
          disabled={!editable}
          InputProps={{ ref }}
        />
      </SearchBox>

      <Form flexStretch onSubmit={formSubmit}>
        {isAddressType && (
          <FormTextInput
            placeholder="Home, Office etc..."
            controlName="userDisplayLocationName"
            autoComplete="address-type"
            disabled={!editable}
            {...inputProps}
          />
        )}
        <FormTextInput
          placeholder="Address Line 1"
          controlName="addrLine1"
          autoComplete="address-line1"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          placeholder="Address Line 2"
          controlName="addrLine2"
          autoComplete="address-line2"
          optional
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="city" // TODO: this will be 'State' in the US
          placeholder="Locality"
          autoComplete="address-level1"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="postcode"
          autoComplete="postal-code"
          disabled={!editable}
          {...inputProps}
        />
        {!lockCountryCode && (
          <FormAutocomplete
            controlName="country"
            options={countriesList}
            getOptionLabel={o => o.name}
            disabled={!editable}
            {...inputProps}
          />
        )}
      </Form>
    </>
  );
}
